import React, { useState, useRef, useEffect } from 'react';
import { devLog } from '../utils/devTools';
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  FormHelperText,
  Avatar,
  CircularProgress,
  Collapse,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useFormContext } from '../context/FormContext';
import { useFileUpload } from '../hooks/useFileUpload';
import DocumentPreview from './DocumentPreview';
import PDFThumbnail from './PDFThumbnail';
import { getInvoiceFiles } from '../utils/invoiceFiles';
import type { FileServerData } from '../types';

interface FileUploadFieldProps {
  title: string;
  type: 'transcriptFiles' | 'exhibitFiles' | 'audioFiles' | 'invoiceFile' | 'orderSubmissionFiles';
  required?: boolean;
  error?: string;
  deponentId?: string; // Optional deponent ID for when uploads are associated with a specific deponent
  attorneyId?: string; // Optional attorney ID for when uploads are associated with a specific attorney (for orderSubmissionFiles)
  maxFileSize?: number; // in MB
}

// Handles both File objects and server response objects
interface FileWithThumbnail {
  // File properties
  name?: string;
  size?: number;
  type?: string;
  lastModified?: number;

  // Server response properties
  originalName?: string;
  filename?: string;
  mimetype?: string;
  url?: string;

  // Upload tracking
  thumbnailUrl?: string;
  deponentId?: string;
  attorneyId?: string;
  serverData?: FileServerData;
  isTemporaryDeponent?: boolean;
  uploadTime?: number;
  fileType?: string;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  title,
  type,
  required = false,
  error,
  deponentId,
  attorneyId,
  maxFileSize = 500
}) => {
  const { handleFileUpload, removeFile, formData } = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Use our enhanced file upload hook
  const { 
    uploadState,
    uploadFiles,
    retryUpload,
    cancelUpload,
    generateThumbnail
  } = useFileUpload();
  
  // Local state for files with thumbnails
  const [filesWithThumbnails, setFilesWithThumbnails] = useState<FileWithThumbnail[]>([]);
  
  // State for document preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  // Generate thumbnails for existing files when formData or dependencies change
  useEffect(() => {
    if (deponentId) {
      devLog(`Looking for files for deponent ID: ${deponentId}`);
    }
    
    if (attorneyId) {
      devLog(`Current formData.attorneys:`, formData.attorneys.map(a => ({ id: a.id, name: a.name })));
    }
    
    async function generateThumbnails() {
      // Get files based on type and deponentId - only inside the effect
      let currentFiles: FileWithThumbnail[] = [];
      
      // Special case for Attorney order submission files
      if (type === 'orderSubmissionFiles' && deponentId && attorneyId) {
        devLog(`Looking for orderSubmissionFiles for attorney ID: ${attorneyId}, deponent ID: ${deponentId}`);
        
        // Find the attorney first
        const attorney = formData.attorneys.find(a => a.id === attorneyId);
        if (attorney) {
          // Find the specific order for this deponent
          const order = attorney.transcriptOrders.find(o => o.deponentId === deponentId);
          if (order && order.orderSubmissionFiles) {
            devLog(`Found order submission files:`, order.orderSubmissionFiles);
            currentFiles = Array.isArray(order.orderSubmissionFiles) ? order.orderSubmissionFiles : [];
          } else {
            devLog(`No order submission files found for this deponent in attorney's orders`);
            currentFiles = [];
          }
        } else {
          console.warn(`Attorney with ID ${attorneyId} not found`);
          currentFiles = [];
        }
      } else if (deponentId) {
        // First check filesWithThumbnails for matching files specifically for this deponent
        if (filesWithThumbnails.length > 0) {
          const filesForThisDeponent = filesWithThumbnails.filter(f => 
            f.deponentId === deponentId
          );
          
          if (filesForThisDeponent.length > 0) {
            devLog(`Found ${filesForThisDeponent.length} files in component state`);
            currentFiles = filesForThisDeponent;
          }
        }
        
        // Also check in formData (to show existing files from previous uploads)
        // First look for a deponent with this ID
        const deponent = formData.deponents.find(d => d.id === deponentId);
        if (deponent) {
          // Get the files for this type from the deponent
          const deponentFiles = deponent[type as keyof typeof deponent];
          if (Array.isArray(deponentFiles) && deponentFiles.length > 0) {
            devLog(`Found ${deponentFiles.length} files in formData for deponent ${deponentId}`);
            
            // Add any files that we don't already have in our local array
            // (avoid duplicates when files exist in both places)
            const existingFilenames = new Set(currentFiles.map(f => f.name ?? f.originalName ?? 'unknown'));

            const newFiles = deponentFiles.filter(f => {
              const filename = f.name ?? (f as FileWithThumbnail).originalName ?? 'unknown';
              return !existingFilenames.has(filename);
            });
            
            if (newFiles.length > 0) {
              devLog(`Adding ${newFiles.length} unique files from formData to currentFiles`);
              currentFiles = [...currentFiles, ...newFiles];
            }
          }
        } else if (deponentId.endsWith(`-${formData.deponents.length}`)) {
          // For temporary IDs, we might need a deeper search for any deponent with this ID
          devLog(`Deep search for files with temporary deponent ID: ${deponentId}`);
          
          // Look for files in any deponent that might have this ID as a property
          for (const d of formData.deponents) {
            for (const fileType of ['transcriptFiles', 'exhibitFiles', 'audioFiles']) {
              const files = d[fileType as keyof typeof d];
              if (Array.isArray(files)) {
                for (const file of files) {
                  if (file && typeof file === 'object' && 'deponentId' in file && file.deponentId === deponentId) {
                    // This file belongs to our temporary deponent
                    const fileAsRecord = file as FileWithThumbnail;
                    const filename = fileAsRecord.name ?? fileAsRecord.originalName ?? 'unknown';
                    devLog(`Found file ${filename} with matching deponentId ${deponentId}`);
                    
                    // Check if we already have this file
                    const existingFilenames = new Set(currentFiles.map(f => f.name ?? f.originalName ?? 'unknown'));
                    if (!existingFilenames.has(filename)) {
                      currentFiles.push(file as FileWithThumbnail);
                    }
                  }
                }
              }
            }
          }
        }
      } else if (type === 'invoiceFile') {
        // Schema-version-aware: reads invoiceFiles on 1.1, falls back to invoiceFile on 1.0.
        currentFiles = getInvoiceFiles(formData) as FileWithThumbnail[];
      }
      
      // Only process if we have files
      if (currentFiles.length === 0) {
        // Keep any manually uploaded files in the local state
        if (filesWithThumbnails.length > 0 && 
            filesWithThumbnails.some(f => f.deponentId === deponentId)) {
          devLog(`Keeping ${filesWithThumbnails.length} files in local state for deponentId ${deponentId}`);
          return;
        }
        setFilesWithThumbnails([]);
        return;
      }
      
      // Generate thumbnails
      const newFilesWithThumbnails: FileWithThumbnail[] = [];
      
      for (let i = 0; i < currentFiles.length; i++) {
        const file = currentFiles[i];
        try {
          // Always create a plain mutable object (File.type is readonly so we can't mutate it directly)
          const fileObj: FileWithThumbnail = file instanceof File
            ? { name: file.name, size: file.size, type: file.type, lastModified: file.lastModified }
            : { ...file, name: file.originalName || file.filename || 'Unknown file', type: file.mimetype || '', size: file.size || 0, deponentId: file.deponentId || deponentId };

          // Detect file type from extension if mimetype is missing
          if (!fileObj.type && fileObj.name) {
            const extension = fileObj.name.split('.').pop()?.toLowerCase() || '';
            // Add mime type based on extension
            if (extension === 'pdf') {
              fileObj.type = 'application/pdf';
            } else if (['doc', 'docx'].includes(extension)) {
              fileObj.type = 'application/msword';
            } else if (['xls', 'xlsx'].includes(extension)) {
              fileObj.type = 'application/vnd.ms-excel';
            } else if (['jpg', 'jpeg'].includes(extension)) {
              fileObj.type = 'image/jpeg';
            } else if (extension === 'png') {
              fileObj.type = 'image/png';
            } else if (extension === 'gif') {
              fileObj.type = 'image/gif';
            } else if (extension === 'txt') {
              fileObj.type = 'text/plain';
            } else if (extension === 'csv') {
              fileObj.type = 'text/csv';
            }
          }
          
          if ('thumbnailUrl' in file && file.thumbnailUrl) {
            newFilesWithThumbnails.push({
              ...fileObj,
              thumbnailUrl: file.thumbnailUrl
            });
          } else {
            try {
              const thumbnailUrl = await generateThumbnail(fileObj);
              newFilesWithThumbnails.push({ ...fileObj, thumbnailUrl });
            } catch (err) {
              console.error(`Error generating thumbnail for ${fileObj.name}:`, err);
              newFilesWithThumbnails.push(fileObj);
            }
          }
        } catch (err) {
          console.error(`Thumbnail generation error for file ${i}:`, err);
          // If thumbnail generation fails, add the file without a thumbnail
          newFilesWithThumbnails.push(file);
        }
      }
      
      // Only update state if we found files
      if (newFilesWithThumbnails.length > 0) {
        devLog(`Setting filesWithThumbnails to ${newFilesWithThumbnails.length} files for deponentId ${deponentId}`);
        
        // Make sure all files have the deponentId
        const filesWithDeponentId = newFilesWithThumbnails.map(f => ({
          ...f,
          deponentId: f.deponentId || deponentId
        }));
        
        setFilesWithThumbnails(filesWithDeponentId);
      }
    }
    
    // Run the thumbnail generation
    generateThumbnails();
    
  // Removing filesWithThumbnails from the dependencies to prevent infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, deponentId, type, attorneyId, generateThumbnail]);

  // Listen for file upload events and track them
  React.useEffect(() => {
    // When files are uploaded, dispatch an event to let DeponentInfo know
    const announceFilesToParent = (files: FileWithThumbnail[], fileType: string) => {
      if (!deponentId || files.length === 0) return;
      
      const uploadEvent = new CustomEvent('file-upload-complete', {
        detail: {
          deponentId,
          type: fileType,
          files: files
        }
      });
      
      devLog(`FileUploadField announcing ${files.length} ${fileType} files for ${deponentId}`);
      document.dispatchEvent(uploadEvent);
    };
    
    // When files change, announce them
    if (filesWithThumbnails.length > 0 && deponentId) {
      announceFilesToParent(filesWithThumbnails, type);
    }
    
    // Listen for file requests
    const handleFileRequest = (event: Event) => {
      const requestEvent = event as CustomEvent;
      if (requestEvent.detail && requestEvent.detail.deponentId === deponentId) {
        // This component has been asked for its files
        devLog(`FileUploadField [${type}] responding to file request for ${deponentId}`);
        
        // Make sure DeponentInfo knows about our files
        if (filesWithThumbnails.length > 0) {
          // Get all files for this deponent from all file upload fields
          const responseEvent = new CustomEvent('files-response', {
            detail: {
              deponentId,
              [type]: filesWithThumbnails.map(file => ({
                ...file,
                deponentId
              }))
            }
          });
          
          // Send back our files
          document.dispatchEvent(responseEvent);
        }
      }
    };
    
    // Add event listener for file requests
    document.addEventListener('request-files', handleFileRequest);
    
    return () => {
      document.removeEventListener('request-files', handleFileRequest);
    };
  }, [deponentId, type, filesWithThumbnails]);

  // Handle file selection through input or drop - memoize to avoid re-creating on every render
  const processFiles = React.useCallback(async (fileList: FileList) => {
    if (!fileList || fileList.length === 0) return;
    
    try {
      // Upload files to server with real tracking
      // Use the core fileType that the server expects
      // Don't include deponentId or other metadata in the fileType
      const fileTypeValue = type;
      
      devLog(`Uploading files with fileType: ${fileTypeValue}, deponentId: ${deponentId || 'none'}, attorneyId: ${attorneyId || 'none'}`);
      
      // Create unique id for the upload component
      const uploadComponentId = deponentId ? 
        `${type}-upload-${deponentId}` : 
        `${type}-upload-global`;
      
      // Set component id on element for easier identification in the DOM
      const rootElement = document.getElementById(fileInputRef.current?.id || '')?.closest('div');
      if (rootElement) {
        rootElement.id = uploadComponentId;
        rootElement.setAttribute('data-deponent-id', deponentId || 'none');
        rootElement.setAttribute('data-file-type', type);
      }
      
      // Create a fresh FileList clone to ensure we're passing proper File objects
      const cleanFileList = new DataTransfer();
      for (let i = 0; i < fileList.length; i++) {
        if (fileList[i] instanceof File) {
          // Clone the file with a new File constructor instead of slice()
          const f = fileList[i];
          const newFile = new File([f], f.name, { 
            type: f.type,
            lastModified: f.lastModified 
          });
          cleanFileList.items.add(newFile);
        }
      }
      
      const result = await uploadFiles(
        cleanFileList.files.length > 0 ? cleanFileList.files : fileList, 
        formData.token || '', 
        fileTypeValue,
        // Callback when thumbnails are generated
        (thumbnail) => {
          // This will be called during upload when thumbnails are ready
          devLog('Thumbnail generated:', thumbnail);
        },
        // Pass deponentId as a separate parameter
        deponentId
      );
      
      if (result.success && result.files) {
        devLog("Upload successful with files:", result.files);
        
        // Save both the original file and server response in the form state
        // This allows us to display the file info while also having metadata from the server
        if (deponentId) {
          const filesWithMetadata = Array.from(fileList).map((origFile, index) => {
            const serverFile = result.files && result.files[index] ? result.files[index] : null;
            const thumbnailUrl = result.thumbnails && result.thumbnails[index] 
              ? result.thumbnails[index] 
              : (index === 0 ? uploadState.thumbnailUrl : undefined);
            
            if (serverFile) {
              // Store the deponent ID as metadata with the file
              return {
                // Don't spread origFile directly as it may not serialize properly
                name: origFile.name,
                size: origFile.size,
                type: origFile.type,
                lastModified: origFile.lastModified,
                
                serverData: serverFile,
                originalName: serverFile.originalName || origFile.name,
                url: serverFile.url,
                thumbnailUrl: thumbnailUrl,
                deponentId: deponentId, // Add deponentId to the file object to track association
                attorneyId: attorneyId, // Add attorneyId for orderSubmissionFiles if available
                isTemporaryDeponent: deponentId.endsWith(`-${formData.deponents.length}`),
                uploadTime: Date.now() // Add timestamp to help with tracking
              };
            }
            return {
              // Don't spread origFile directly as it may not serialize properly
              name: origFile.name,
              size: origFile.size,
              type: origFile.type,
              lastModified: origFile.lastModified,
              
              thumbnailUrl: thumbnailUrl,
              deponentId: deponentId,
              attorneyId: attorneyId, // Add attorneyId for orderSubmissionFiles if available
              isTemporaryDeponent: deponentId.endsWith(`-${formData.deponents.length}`),
              uploadTime: Date.now() // Add timestamp to help with tracking
            };
          });
          
          // Always save files to form context, even for temporary deponent IDs
          devLog(`Saving files for deponent ID: ${deponentId}`);
          handleFileUpload(type, filesWithMetadata, deponentId);
          
          // Also update our local state to show the files immediately
          // First filter out any existing files with same names to prevent duplicates
          setFilesWithThumbnails(prev => {
            const newFilenames = filesWithMetadata.map(f => f.name ?? 'unknown');

            const uniqueExistingFiles = prev.filter(f => {
              const filename = f.name ?? f.originalName ?? 'unknown';
              return !newFilenames.includes(filename);
            });
            // Add the new files to the filtered list
            return [...uniqueExistingFiles, ...filesWithMetadata];
          });
        } else if (type === 'invoiceFile' && fileList.length > 0) {
          // Invoice uploads are multi-file capable; append all files to
          // formData.invoiceFiles via handleFileUpload's invoice branch.
          const filesWithMetadata = Array.from(fileList).map((origFile, index) => {
            const serverFile = result.files && result.files[index] ? result.files[index] : null;
            const thumbnailUrl = result.thumbnails && result.thumbnails[index]
              ? result.thumbnails[index]
              : (index === 0 ? uploadState.thumbnailUrl : undefined);

            if (serverFile) {
              return {
                name: origFile.name,
                size: origFile.size,
                type: origFile.type,
                lastModified: origFile.lastModified,
                serverData: serverFile,
                originalName: serverFile.originalName || origFile.name,
                url: serverFile.url,
                thumbnailUrl,
                fileType: type,
                uploadTime: Date.now()
              };
            }
            console.error(
              'INVOICE UPLOAD ERROR: Server response missing file data for invoice upload.',
              'Original upload result:', result
            );
            return {
              name: origFile.name,
              size: origFile.size,
              type: origFile.type,
              lastModified: origFile.lastModified,
              thumbnailUrl,
              fileType: type,
              uploadTime: Date.now()
            };
          });
          handleFileUpload(type, filesWithMetadata);
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      // Error is already tracked in uploadState
    }
  // ESLINT NOTE: Intentionally omitting formData.deponents.length and uploadState.thumbnailUrl from deps.
  // Adding them would recreate this callback whenever a thumbnail loads or a deponent is added,
  // which could interrupt in-progress uploads. Both values are only used for metadata tagging,
  // not for controlling upload behavior.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFiles, formData.token, deponentId, attorneyId, type, handleFileUpload]);

  const handleFileChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      processFiles(files);
    }
  }, [processFiles]);

  const handleRemoveFile = React.useCallback((index: number) => {
    if (type === 'orderSubmissionFiles' && deponentId && attorneyId) {
      // For order submission files, combine deponentId and attorneyId for proper removal
      const compositeId = `${attorneyId}:${deponentId}`;
      devLog(`Removing order submission file at index ${index} with composite ID ${compositeId}`);
      removeFile(type, index, compositeId);
    } else if (deponentId) {
      // For regular deponent-specific files
      devLog(`Removing file at index ${index} for deponent ${deponentId}`);
      removeFile(type, index, deponentId);
    } else {
      // For global files
      devLog(`Removing global file at index ${index}`);
      removeFile(type, index);
    }
  }, [removeFile, type, deponentId, attorneyId]);

  const formatFileSize = React.useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);
  
  const formatTimeRemaining = React.useCallback((seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) return 'calculating...';
    if (seconds < 1) return 'almost done';
    
    // Sanity check - if time is absurdly high, just show a reasonable estimate
    if (seconds > 7200) { // More than 2 hours
      return 'over 2 hours left';
    }
    
    if (seconds < 60) {
      return `${Math.round(seconds)} seconds left`;
    } else if (seconds < 3600) {
      const minutes = Math.round(seconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} left`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.round((seconds % 3600) / 60);
      return `${hours}h ${minutes}m left`;
    }
  }, []);
  
  // Format file name to preserve extension when truncated
  const formatFileName = React.useCallback((fileName: string): string => {
    // Check if the filename has our special marker for preserving extension
    const specialMarkerIndex = fileName.indexOf('[...]');
    
    if (specialMarkerIndex !== -1) {
      // Extract base name and extension
      const baseName = fileName.substring(0, specialMarkerIndex);
      const extension = fileName.substring(specialMarkerIndex + 5); // 5 is length of '[...]'
      
      // On display, we can create a clean version that ensures extension visibility
      // Note: CSS will handle truncation, but we ensure extension would be visible
      return `${baseName}...${extension}`;
    }
    
    return fileName;
  }, []);

  const handleDrop = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  const handleDragOver = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  // Get the appropriate icon based on file type - memoize to prevent re-renders
  // Handle opening the preview dialog
  const handleOpenPreview = (file: FileWithThumbnail) => {
    devLog('Opening preview for file:', file);
    
    // For files that came from the server, we need to fetch them
    if (file instanceof File) {
      devLog('File is a File object, using directly');
      setPreviewFile(file);
      setPreviewOpen(true);
    } else if (file.url) {
      devLog('File has URL, fetching from server:', file.url);
      // For files with URLs, we'll fetch them and convert to Blob
      fetch(file.url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
          }
          return response.blob();
        })
        .then(blob => {
          const fileName = file.name || file.originalName || file.filename || 'file';
          const fileType = file.type || file.mimetype || 'application/octet-stream';
          devLog(`Created blob with type ${fileType} for ${fileName}`);
          const newFile = new File([blob], fileName, { type: fileType });
          setPreviewFile(newFile);
          setPreviewOpen(true);
        })
        .catch(err => {
          console.error('Error fetching file for preview:', err);
          // Still open the dialog but with error state
          setPreviewFile(null);
          setPreviewOpen(true);
        });
    } else {
      console.error('File has no URL and is not a File object:', file);
      setPreviewFile(null);
      setPreviewOpen(true);
    }
  };

  // Handle closing the preview dialog
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  // Check if a file is previewable
  const isPreviewable = (file: FileWithThumbnail): boolean => {
    const fileType = file.type || file.mimetype || '';
    const fileName = file.name || file.originalName || file.filename || '';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    return (
      fileType === 'application/pdf' || 
      extension === 'pdf'
    );
  };

  const getFileIcon = React.useCallback((file: FileWithThumbnail, thumbnailUrl?: string) => {
    const fileThumbUrl = file.thumbnailUrl || thumbnailUrl;
    
    if (fileThumbUrl && typeof fileThumbUrl === 'string' && fileThumbUrl.length > 0 && 
        (fileThumbUrl.startsWith('data:') || fileThumbUrl.startsWith('/file-icons/') || fileThumbUrl.startsWith('http'))) {
      return (
        <Avatar 
          src={fileThumbUrl} 
          variant="rounded" 
          sx={{ 
            width: 62, 
            height: 70, 
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          alt={file.name || file.originalName || file.filename || 'File'}
        />
      );
    }
    
    const fileType = file.type || file.mimetype || '';
    const fileName = file.name || file.originalName || file.filename || '';
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    // Format the filename consistently for display
    const formatFilename = (name: string) => {
      if (name.length <= 12) return name;
      
      const nameParts = name.split('.');
      const ext = nameParts.pop() || '';
      const baseName = nameParts.join('.');
      return baseName.length > 10 
        ? baseName.substring(0, 8) + '...' + (ext ? '.' + ext : '')
        : baseName + (ext ? '.' + ext : '');
    };
    
    // Text files (txt, csv)
    if (['txt', 'csv', 'text'].includes(extension) || fileType.includes('text')) {
      return (
        <Box sx={{ 
          width: 50, 
          height: 70,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          backgroundColor: '#f8f8f8',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '25px', // Taller header
            backgroundColor: '#4285f4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>
              {/* Show the abbreviated filename with extension */}
              {(() => {
                const nameParts = fileName.split('.');
                const ext = nameParts.pop() || '';
                const baseName = nameParts.join('.');
                const maxBaseLength = 8;
                return baseName.length > maxBaseLength
                  ? baseName.substring(0, maxBaseLength) + '...' + (ext || '')
                  : baseName + (ext ? '.' + ext : '');
              })()}
            </Typography>
          </Box>
          <Box sx={{ mt: 3, px: 1, width: '100%' }}> {/* Increased top margin */}
            <Typography variant="caption" sx={{ fontSize: '11px', display: 'block', textAlign: 'center', color: '#444', mt: 1.5 }}>
              Text content
            </Typography>
          </Box>
          {/* Display file size with larger font */}
          <Typography variant="caption" sx={{ fontSize: '13px', display: 'block', textAlign: 'center', color: '#555', mt: 'auto', mb: 1 }}>
            {formatFileSize(file.size || 0)}
          </Typography>
        </Box>
      );
    } 
    // Image files
    else if (fileType.startsWith('image/')) {
      return (
        <Box sx={{ 
          width: 50, 
          height: 70,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          backgroundColor: '#f5f9ff',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '16px',
            backgroundColor: '#03a9f4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '8px' }}>
              {extension.toUpperCase() || 'IMG'}
            </Typography>
          </Box>
          <ImageIcon color="primary" sx={{ fontSize: 24, mt: 2 }} />
          <Typography variant="caption" sx={{ fontSize: '7px', display: 'block', textAlign: 'center', fontWeight: 'bold', mt: 1 }}>
            {formatFilename(fileName)}
          </Typography>
        </Box>
      );
    } 
    // PDF files
    else if (fileType === 'application/pdf' || extension === 'pdf') {
      return (
        <Box sx={{ 
          width: 50, 
          height: 70,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          backgroundColor: '#fff5f5',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '16px',
            backgroundColor: '#f44336',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '8px' }}>
              PDF
            </Typography>
          </Box>
          <PdfIcon color="error" sx={{ fontSize: 24, mt: 2 }} />
          <Typography variant="caption" sx={{ fontSize: '7px', display: 'block', textAlign: 'center', fontWeight: 'bold', mt: 1 }}>
            {formatFilename(fileName)}
          </Typography>
        </Box>
      );
    } 
    // Word documents
    else if (['doc', 'docx'].includes(extension) || fileType.includes('word')) {
      return (
        <Box sx={{ 
          width: 50, 
          height: 70,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          backgroundColor: '#f0f8ff',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '25px', // Taller header
            backgroundColor: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>
              {/* Show abbreviated filename with extension */}
              {(() => {
                const nameParts = fileName.split('.');
                const ext = nameParts.pop() || '';
                const baseName = nameParts.join('.');
                const maxBaseLength = 8;
                return baseName.length > maxBaseLength
                  ? baseName.substring(0, maxBaseLength) + '..' + (ext || '')
                  : baseName + (ext ? '.' + ext : '');
              })()}
            </Typography>
          </Box>
          <Box sx={{ mt: 3, px: 1, width: '100%' }}> {/* Increased top margin */}
            <Typography variant="caption" sx={{ fontSize: '11px', display: 'block', textAlign: 'center', color: '#1976d2', mt: 1.5 }}>
              Word doc
            </Typography>
          </Box>
          {/* Display file size with larger font */}
          <Typography variant="caption" sx={{ fontSize: '13px', display: 'block', textAlign: 'center', color: '#555', mt: 'auto', mb: 1 }}>
            {formatFileSize(file.size || 0)}
          </Typography>
        </Box>
      );
    } 
    // All other file types
    else {
      return (
        <Box sx={{ 
          width: 50, 
          height: 70,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          backgroundColor: '#fafafa',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '16px',
            backgroundColor: '#757575',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '8px' }}>
              {extension.toUpperCase() || 'FILE'}
            </Typography>
          </Box>
          <FileIcon color="info" sx={{ fontSize: 24, mt: 2 }} />
          <Typography variant="caption" sx={{ fontSize: '7px', display: 'block', textAlign: 'center', fontWeight: 'bold', mt: 1 }}>
            {formatFilename(fileName)}
          </Typography>
        </Box>
      );
    }
  }, [formatFileSize]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
        {required && (
          <Typography component="span" color="error" variant="inherit">
            *
          </Typography>
        )}
      </Typography>
      
      {/* Error message from failed upload */}
      <Collapse in={!!uploadState.error}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => retryUpload()}
              startIcon={<RefreshIcon />}
            >
              Retry
            </Button>
          }
        >
          {uploadState.error}
        </Alert>
      </Collapse>
      
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          textAlign: 'center',
          cursor: uploadState.isUploading ? 'default' : 'pointer',
          borderStyle: 'dashed',
          borderColor: error ? 'error.main' : (uploadState.isUploading ? 'primary.main' : 'divider'),
          bgcolor: 'action.hover',
          '&:hover': {
            borderColor: uploadState.isUploading ? 'primary.main' : 'primary.main',
            bgcolor: uploadState.isUploading ? 'action.hover' : 'action.selected'
          },
          minHeight: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={() => !uploadState.isUploading && fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input 
          type="file" 
          ref={fileInputRef}
          onChange={handleFileChange}
          multiple
          hidden
          disabled={uploadState.isUploading}
        />
        {uploadState.isUploading ? (
          <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
              {/* Background circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={70}
                thickness={4}
                sx={{ 
                  color: (theme) => theme.palette.grey[200],
                  position: 'absolute',
                  left: 0
                }}
              />
              {/* Foreground progress circle */}
              <CircularProgress 
                variant="determinate" 
                value={uploadState.progress} 
                size={70} 
                thickness={4} 
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" component="div" color="primary">
                  {`${Math.round(uploadState.progress)}%`}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {uploadState.fileName ? 
                  `Uploading ${formatFileName(uploadState.fileName)}` : 
                  'Uploading...'}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                {/* Show bytes uploaded / total bytes */}
                {uploadState.bytesUploaded !== undefined && uploadState.bytesTotal !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(uploadState.bytesUploaded)} of {formatFileSize(uploadState.bytesTotal)} uploaded
                  </Typography>
                )}
                
                {/* Show speed and time remaining */}
                {uploadState.timeRemaining !== undefined && uploadState.uploadSpeed !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    {uploadState.uploadSpeed && uploadState.uploadSpeed > 100 ? 
                      `${formatFileSize(uploadState.uploadSpeed)}/s · ${formatTimeRemaining(uploadState.timeRemaining || 0)}` : 
                      'Calculating upload speed...'}
                  </Typography>
                )}
              </Box>
              
              <Button 
                size="small" 
                startIcon={<CancelIcon />} 
                color="error" 
                onClick={(e) => {
                  e.stopPropagation();  // Prevent the click from triggering file selection
                  cancelUpload();
                }}
              >
                Cancel
              </Button>
            </Box>
            
            {uploadState.thumbnailUrl && (
              <Box sx={{ mt: 2 }}>
                <Avatar 
                  src={uploadState.thumbnailUrl} 
                  variant="rounded" 
                  sx={{ width: 80, height: 80, mx: 'auto' }}
                  alt="File preview"
                />
              </Box>
            )}
          </Box>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              Drag and drop files here or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Max file size: {maxFileSize}MB
            </Typography>
          </>
        )}
      </Paper>

      {error && (
        <FormHelperText error>{error}</FormHelperText>
      )}

      {/* File List with Thumbnails */}
      {filesWithThumbnails.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" gutterBottom>
            Uploaded Files
          </Typography>
          <List>
            {filesWithThumbnails.map((file, index) => (
              <ListItem 
                key={`${deponentId || 'global'}-${type}-${index}-${file.name || file.originalName || file.filename || index}`}
                component={Paper}
                variant="outlined"
                sx={{ mb: 1, p: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  {(() => {
                    const fileType = file.type || file.mimetype || '';
                    const fileName = file.name || file.originalName || file.filename || '';
                    const extension = fileName.split('.').pop()?.toLowerCase() || '';
                    const isPdf = fileType === 'application/pdf' || extension === 'pdf';
                    
                    if (isPdf && file instanceof File) {
                      return <PDFThumbnail file={file} width={50} />;
                    }
                    
                    return getFileIcon(file, file.thumbnailUrl);
                  })()}
                </Box>
                <ListItemText
                  primary={file.name || file.originalName || file.filename || 'Unnamed file'}
                  secondary={file.size ? formatFileSize(file.size) : ''}
                />
                <ListItemSecondaryAction>
                  {isPreviewable(file) && (
                    <IconButton
                      edge="end"
                      color="primary"
                      onClick={() => handleOpenPreview(file)}
                      aria-label="Preview file"
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  )}
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => {
                      // First remove from local state for immediate UI update
                      setFilesWithThumbnails(prev => prev.filter((_, i) => i !== index));
                      // Then remove from form context
                      handleRemoveFile(index);
                    }}
                    aria-label="Remove file"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Document Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Document Preview
          <IconButton
            aria-label="close"
            onClick={handleClosePreview}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewFile ? (
            <DocumentPreview file={previewFile} width={600} />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="error" variant="body1" gutterBottom>
                Unable to preview this document
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The file couldn&apos;t be loaded for preview. Try downloading it instead.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FileUploadField;